import React from "react";
import { TRANSFORM_LINKING } from "@sscs/report/constants";
import { BugInfo } from "@sscs/report/ui";
import {
  Card,
  DataTable,
  DataTableColumn,
  LoadingDots,
  SearchChip,
  Table,
  Tooltip,
} from "@sscs/ui";
import useSearch from "../../../hooks/useSearch";
import { InsightDetails, VulnerabilityInsight } from "../../../types";
import styles from "./details.module.scss";
import PolicyViolations from "./PolicyViolations/PolicyViolations";

type VulnerabilityProps = {
  issue: VulnerabilityInsight;
  issueDetails: InsightDetails | undefined;
};

const BUG_COLUMNS: DataTableColumn[] = [
  { id: "type", label: "Type", width: "230px" },
  { id: "name", label: "Name", width: "100%" },
  { id: "introduced", label: "Introduced in", width: "225px" },
  { id: "resolved", label: "Resolved in", width: "225px" },
];

const BugsTable = ({
  details,
}: {
  details: InsightDetails | undefined;
}): React.JSX.Element | null => {
  if (!details) {
    return <LoadingDots />;
  }

  if (details.issue_type !== "vulnerability") {
    return null;
  }

  return (
    <DataTable title="Vulnerable software" columns={BUG_COLUMNS}>
      {details.bugs.map((item, index) => (
        <Table.Row hover key={index}>
          <Table.Cell>
            {item.linking ? TRANSFORM_LINKING[item.linking] : "Component"}
          </Table.Cell>
          <Table.Cell>
            {item.product} {item.version}
          </Table.Cell>
          <Table.Cell>
            <BugInfo bugInfo={item.introduced} bugType="introduced" />
          </Table.Cell>
          <Table.Cell>
            <BugInfo bugInfo={item.resolved} bugType="resolved" />
          </Table.Cell>
        </Table.Row>
      ))}
    </DataTable>
  );
};

const Vulnerability = ({
  issue,
  issueDetails,
}: VulnerabilityProps): React.JSX.Element => {
  const { handleChipClick } = useSearch();

  const handleCweClick = (cweId: string) => {
    handleChipClick("cwe_identifier", {
      operation: "is_any",
      values: [cweId],
    });
  };

  return (
    <>
      <div className={styles["description-and-issues-container"]}>
        <Card.Card>
          <Card.Title>Vulnerability description</Card.Title>
          <Card.Content>
            <div>{issue.long_description}</div>
            <div className={styles["cwe-list"]}>
              {Object.values(issue.cwe).map((cwe) => (
                <Tooltip
                  key={cwe.cwe_id}
                  title={
                    <>
                      <div>
                        <b>{cwe.name}</b>
                      </div>
                      <div>{cwe.description}</div>
                    </>
                  }
                >
                  <SearchChip
                    label={cwe.cwe_id}
                    onClick={() => handleCweClick(cwe.cwe_id)}
                  />
                </Tooltip>
              ))}
            </div>
          </Card.Content>
        </Card.Card>
        <PolicyViolations
          violations={issue.caused_violations}
          evaluations={issue.risk_assessment_evaluation}
        />
      </div>
      <BugsTable details={issueDetails} />
    </>
  );
};

export default Vulnerability;
